import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

const DEFAULT_ZOOM_FACTOR = 1.15;
const PIXELS_PER_WHEEL_STEP = 100;
const PIXELS_PER_WHEEL_LINE = PIXELS_PER_WHEEL_STEP / 3;

const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

const getWheelDeltaInPixels = (event, container) => {
  if (event.deltaMode === 1) {
    return event.deltaY * PIXELS_PER_WHEEL_LINE;
  }
  if (event.deltaMode === 2) {
    return event.deltaY * (container?.clientHeight || PIXELS_PER_WHEEL_STEP);
  }
  return event.deltaY;
};

const getZoomAnchorElement = (target, container) => {
  if (typeof Element === 'undefined' || !(target instanceof Element)) {
    return null;
  }

  const anchorElement = target.closest('[data-wheel-zoom-anchor]');
  return anchorElement && container.contains(anchorElement) ? anchorElement : null;
};

const findZoomAnchorElement = (container, anchorKey) => {
  if (!anchorKey) {
    return null;
  }

  return Array.from(container.querySelectorAll('[data-wheel-zoom-anchor]'))
    .find((element) => element.dataset.wheelZoomAnchor === anchorKey) || null;
};

/**
 * Uses the mouse wheel to zoom a visual viewer around the viewport center.
 */
const useWheelZoom = ({
  containerRef,
  enabled = true,
  maximumZoom,
  minimumZoom,
  setZoom,
  zoom,
  zoomFactor = DEFAULT_ZOOM_FACTOR,
}) => {
  const pendingPositionRef = useRef(null);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  const restoreWheelZoomPosition = useCallback(({ clear = false } = {}) => {
    const container = containerRef.current;
    const pendingPosition = pendingPositionRef.current;
    if (!container || !pendingPosition) {
      return;
    }

    const anchorElement = findZoomAnchorElement(container, pendingPosition.anchorKey);
    const anchorBounds = anchorElement?.getBoundingClientRect();
    const hasMeasurableAnchor = anchorBounds && anchorBounds.width > 0 && anchorBounds.height > 0;
    const requestedScrollLeft = hasMeasurableAnchor
      ? container.scrollLeft + anchorBounds.left +
        pendingPosition.anchorRatioX * anchorBounds.width - pendingPosition.clientX
      : pendingPosition.contentX * pendingPosition.targetZoom - pendingPosition.pointerX;
    const requestedScrollTop = hasMeasurableAnchor
      ? container.scrollTop + anchorBounds.top +
        pendingPosition.anchorRatioY * anchorBounds.height - pendingPosition.clientY
      : pendingPosition.contentY * pendingPosition.targetZoom - pendingPosition.pointerY;
    const maximumScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    const maximumScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);

    container.scrollLeft = clamp(requestedScrollLeft, 0, maximumScrollLeft);
    container.scrollTop = clamp(requestedScrollTop, 0, maximumScrollTop);

    if (clear) {
      pendingPositionRef.current = null;
    }
  }, [containerRef]);

  useLayoutEffect(() => {
    if (!pendingPositionRef.current) {
      return undefined;
    }

    // Emotion/transform styles and scroll extents may not be measurable until
    // the next frame. Keep the anchor pending and correct it again after paint.
    restoreWheelZoomPosition();
    const animationFrame = window.requestAnimationFrame(() => {
      restoreWheelZoomPosition({ clear: true });
    });
    return () => window.cancelAnimationFrame(animationFrame);
  }, [restoreWheelZoomPosition, zoom]);

  const handleWheel = useCallback((event) => {
    if (!enabled || !event.deltaY) {
      return;
    }

    event.preventDefault();
    const currentZoom = zoomRef.current;
    const normalizedDelta = getWheelDeltaInPixels(event, containerRef.current);
    const requestedZoom = currentZoom * Math.pow(
      zoomFactor,
      -normalizedDelta / PIXELS_PER_WHEEL_STEP
    );
    const nextZoom = Number(clamp(requestedZoom, minimumZoom, maximumZoom).toFixed(4));
    if (nextZoom === currentZoom) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    const bounds = container.getBoundingClientRect();
    const pointerX = container.clientWidth / 2;
    const pointerY = container.clientHeight / 2;
    const clientX = bounds.left + pointerX;
    const clientY = bounds.top + pointerY;
    const centerTarget = typeof document !== 'undefined'
      ? document.elementFromPoint?.(clientX, clientY)
      : null;
    const anchorElement = getZoomAnchorElement(centerTarget, container);
    const anchorBounds = anchorElement?.getBoundingClientRect();
    pendingPositionRef.current = {
      anchorKey: anchorElement?.dataset.wheelZoomAnchor || null,
      anchorRatioX: anchorBounds?.width
        ? clamp((clientX - anchorBounds.left) / anchorBounds.width, 0, 1)
        : 0,
      anchorRatioY: anchorBounds?.height
        ? clamp((clientY - anchorBounds.top) / anchorBounds.height, 0, 1)
        : 0,
      clientX,
      clientY,
      contentX: (container.scrollLeft + pointerX) / currentZoom,
      contentY: (container.scrollTop + pointerY) / currentZoom,
      pointerX,
      pointerY,
      targetZoom: nextZoom,
    };

    zoomRef.current = nextZoom;
    setZoom(nextZoom);
  }, [containerRef, enabled, maximumZoom, minimumZoom, setZoom, zoomFactor]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) {
      return undefined;
    }

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [containerRef, enabled, handleWheel]);

  useEffect(() => () => {
    pendingPositionRef.current = null;
  }, []);

};

export default useWheelZoom;
